import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon' 
import { Input, Select, Toggle } from '@/components/ui'
import { SingleLineAddress } from '@/components/Address'
import { selectQuittancesByIds, updateBatchRents, MODES_REGLEMENT } from './quittancesSlice'

export default ({ids = [], onClose = () => {}, onSuccess = () => {}}) => {
  const dispatch = useDispatch()
  const quittances = useSelector(state => selectQuittancesByIds(state, ids))
  const errors = useSelector((state) => state.quittances.requestStatus.errors)
  const isUpdating = useSelector((state) => state.quittances.loadingStatus.updateBatchRents)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const warningFieldNames = useRef(new Set())
  const [selectedQuittances, setSelectedQuittances] = useState([])
  
  // Obtenir le dernier jour du mois courant pour la date de délivrance par défaut
  const lastDayOfMonth = DateTime.now().endOf('month') 
  
  const { handleSubmit, reset, control, getValues, setValue, formState: { errors: formErrors } } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    setSelectedQuittances(quittances)
    quittances.forEach(quittance => {
      const fieldBaseName = `quittance_${quittance.id}`
      const delivranceFieldName = `${fieldBaseName}_date_delivrance`
      if(quittance.month !== DateTime.fromISO(lastDayOfMonth).month) {
        setValue(delivranceFieldName, DateTime.now().toISODate())
        warningFieldNames.current = new Set([
          ...warningFieldNames.current,
          delivranceFieldName
        ])

      } else {
        setValue(delivranceFieldName, lastDayOfMonth)

      }
      setValue(`${fieldBaseName}_mode_reglement`, 'virement')
      setValue(`${fieldBaseName}_montant_loyer`, quittance.montant_loyer)
      setValue(`${fieldBaseName}_montant_charges`, quittance.montant_charges)
    })
  }, [quittances])
  
  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        reset()
        onSuccess()
      }
    }
  }, [isUpdating, errors, onSuccess, reset])
  
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)
      
      // Transformer les données du formulaire pour le format attendu par l'API
      const quittancesData = {}
      
      selectedQuittances.forEach(quittance => {
        quittancesData[quittance.id] = {
          montant_loyer: formData[`quittance_${quittance.id}_montant_loyer`],
          montant_charges: formData[`quittance_${quittance.id}_montant_charges`],
          date_paiement: formData[`quittance_${quittance.id}_date_paiement`],
          date_delivrance: formData[`quittance_${quittance.id}_date_delivrance`],
          mode_reglement: formData[`quittance_${quittance.id}_mode_reglement`],
          force_notification_to_locataires: formData[`quittance_${quittance.id}_force_notification_to_locataires`]
        }
      })
      
      await dispatch(updateBatchRents({
        ids: ids,
        data: quittancesData
      }))
    } catch (error) {
      setIsSubmitting(false)
    }
  }
  
  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  // Formater la date (mois et année)
  const formatPeriod = (year, month) => {
    const date = new Date(year, month - 1)
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Générer les quittances sélectionnées</h2>
        </div>
        
        {selectedQuittances.length === 0 ? (
          <p className="text-gray-500 mb-4">Aucune quittance sélectionnée</p>
        ) : (
          <>
            <div className="card bg-base-100 border border-primary-200 rounded-none mb-4 min-h-[450px] overflow-y-auto">
              <div className="p-4">
                <div className="space-y-6 mb-6">
                  {selectedQuittances.map(quittance => (
                    <div key={quittance.id} className="border border-primary-500 p-4 shadow-sm">
                      <div className="flex items-center mb-2">
                        <span className="font-medium"> 
                          {quittance.locataires && quittance.locataires.length > 0
                            ? quittance.locataires.map(l => l.nom_complet).join(', ')
                            : 'Locataire inconnu'} -
                          <span className="ml-2">{formatPeriod(quittance.year, quittance.month) || 'Non spécifiée'}</span>
                        </span>
                        <div className="text-sm font-bold ml-4">
                          <SingleLineAddress data={quittance.bien} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Input.Currency 
                            name={`quittance_${quittance.id}_montant_loyer`}
                            label="Montant du loyer"
                            control={control}
                            rules={{
                              required: "Le montant du loyer est requis."
                            }}
                            validator={{hasError, getErrorMessage}}
                          />
                          <Input.Currency 
                            name={`quittance_${quittance.id}_montant_charges`}
                            label="Montant des charges"
                            control={control}
                            rules={{
                              required: "Le montant des charges est requis."
                            }}
                            validator={{hasError, getErrorMessage}}
                          />
                        </div>
                      
                        <div className="space-y-2">
                          <Input.Date
                            name={`quittance_${quittance.id}_date_paiement`}
                            label="Date de paiement"
                            control={control}
                            rules={{
                              required: "La date de paiement est requise"
                            }}
                            validator={{hasError, getErrorMessage}}
                          />
                          <div className="space-y-2">
                            <Input.Date
                              name={`quittance_${quittance.id}_date_delivrance`}
                              label="Date de délivrance"
                              control={control}
                              rules={{
                                required: "La date de délivrance est requise"
                              }}
                              validator={{hasError, getErrorMessage}}
                            />
                            { warningFieldNames.current.has(`quittance_${quittance.id}_date_delivrance`) && 
                              <div className="text-warning text-sm">Penser à vérifier la date de délivrance !</div>
                            }
                          </div>
                        </div>
                        <div className="">
                          <Select.Form
                            name={`quittance_${quittance.id}_mode_reglement`}
                            control={control}
                            label="Mode de règlement"
                            options={MODES_REGLEMENT}
                            validator={{hasError, getErrorMessage}}
                          />
                          <Toggle.Form 
                            name={`quittance_${quittance.id}_force_notification_to_locataires`}
                            control={control}
                            label="Envoyé aussitôt au(x) locataire(s)"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={`btn ${isUpdating ? 'loading' : ''} btn-primary text-white p-2`}
                disabled={isUpdating}
              >
                {isUpdating ? 'Génération...' : 'Générer les quittances'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
